import { clsx, type ClassValue } from "clsx";
import { Timestamp } from "firebase/firestore";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatTimeEST(
  time: Timestamp | Date | string | null | undefined,
  includeDate: boolean = false,
): string {
  if (time === null || time === undefined) {
    return "";
  }

  let date: Date;

  if (time instanceof Timestamp) {
    date = time.toDate();
  } else if (time instanceof Date) {
    date = time;
  } else if (typeof time === "string") {
    if (time.includes("AM") || time.includes("PM")) {
      return time;
    }
    if (time.includes(":")) {
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    return time;
  } else {
    return "";
  }

  if (includeDate) {
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/New_York",
    });
  }

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/New_York",
  });
}

/**
 * Formats a date in EST timezone without time component.
 * Returns a human-readable date format like "November 27, 2025".
 *
 * @param date - Timestamp, Date object, or null/undefined
 * @returns Formatted date string in EST timezone
 *
 * @example
 * formatDateEST(timestamp); // Returns: "November 27, 2025"
 */
export function formatDateEST(date: Timestamp | Date | null | undefined): string {
  if (date === null || date === undefined) {
    return "";
  }

  let dateObj: Date;

  if (date instanceof Timestamp) {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return "";
  }

  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "America/New_York",
  });
}

/**
 * Formats a date and time in MM/DD/YY, HH:MM AM/PM format in EST timezone.
 * Used in Projects (New UI) section for consistent date/time display.
 *
 * @param date - Timestamp, Date object, string, or null/undefined
 * @returns Formatted date string like "11/04/25, 12:00 PM"
 *
 * @example
 * formatDateTimeShort(timestamp); // Returns: "11/04/25, 12:00 PM"
 */
export function formatDateTimeShort(date: Timestamp | Date | string | null | undefined): string {
  if (date === null || date === undefined) {
    return "";
  }

  let dateObj: Date;

  if (date instanceof Timestamp) {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === "string") {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return date;
    }
  } else {
    return "";
  }

  return dateObj.toLocaleString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/New_York",
  });
}

export function formatPhoneNumber(phoneNumber: string | null | undefined): string {
  if (!phoneNumber) {
    return "";
  }

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Check if it's a valid 10-digit US phone number
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  // Handle US phone numbers with country code (11 digits starting with 1)
  // Only format if the remaining 10 digits form a valid US phone number
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    const withoutCountryCode = cleaned.slice(1);
    return `${withoutCountryCode.slice(0, 3)}-${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6, 10)}`;
  }

  // If it's not 10 or 11 digits, return the original cleaned number
  if (cleaned.length > 0) {
    return cleaned;
  }

  return "";
}

/**
 * Converts a date string from an HTML date input (YYYY-MM-DD) to a Date object
 * at noon in New York timezone. Using noon prevents any date shifting issues
 * when converting between timezones.
 *
 * @param dateString - Date string in YYYY-MM-DD format from HTML date input
 * @returns Date object at noon EST/EDT on the specified date
 *
 * @example
 * // User selects Jan 15, 2025 in form
 * const date = dateStringToNewYorkDate("2025-01-15");
 * // Returns: 2025-01-15 12:00:00 EST (17:00:00 UTC during EST, 16:00:00 UTC during EDT)
 */
export function dateStringToNewYorkDate(dateString: string): Date {
  if (!dateString || dateString.trim() === "") {
    throw new Error("Date string is required");
  }

  // Parse the date string (YYYY-MM-DD)
  const parts = dateString.split("-");
  if (parts.length !== 3) {
    throw new Error(`Invalid date string format: "${dateString}". Expected YYYY-MM-DD format.`);
  }

  const year = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10);
  const day = Number.parseInt(parts[2], 10);

  // Validate that we have valid numbers
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    throw new Error(`Invalid date string format: "${dateString}". Expected YYYY-MM-DD format.`);
  }

  // To get noon in New York time, we need to find what UTC time corresponds to noon NY time on this date
  // We'll create a date in NY timezone and convert back to get the correct UTC timestamp

  // First, create a rough estimate (assume EST = UTC-5, or EDT = UTC-4 during daylight saving)
  const estimatedDate = new Date(Date.UTC(year, month - 1, day, 17, 0, 0)); // Noon EST = 5pm UTC

  // Now check what this looks like in NY timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const formattedParts = formatter.formatToParts(estimatedDate);
  const partMap: Record<string, string> = {};
  formattedParts.forEach((part) => {
    partMap[part.type] = part.value;
  });

  const nyHour = Number.parseInt(partMap.hour, 10);

  // If the hour isn't 12, we need to adjust (likely due to DST)
  if (nyHour !== 12) {
    const hourDiff = 12 - nyHour;
    return new Date(estimatedDate.getTime() + hourDiff * 60 * 60 * 1000);
  }

  return estimatedDate;
}

/**
 * Converts a Firestore Timestamp to a date string (YYYY-MM-DD) suitable for
 * HTML date inputs, interpreting the date in New York timezone.
 *
 * @param timestamp - Firestore Timestamp object
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * // Firestore has: 2025-01-15 17:00:00 UTC (noon EST)
 * const dateStr = timestampToDateString(timestamp);
 * // Returns: "2025-01-15"
 */
export function timestampToDateString(timestamp: Timestamp | null | undefined): string {
  if (!timestamp) {
    return "";
  }

  const date = timestamp.toDate();

  // Format in New York timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const dateParts: Record<string, string> = {};
  parts.forEach((part) => {
    dateParts[part.type] = part.value;
  });

  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
}

export function formatTimeTo12Hour(time24: string): string {
  if (!/^\d{1,2}:\d{2}$/.test(time24)) return time24; // invalid format

  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${period}`;
}

/**
 * Converts a Firestore Timestamp to a datetime-local string (YYYY-MM-DDTHH:mm)
 * in EST/EDT timezone for use in HTML datetime-local inputs.
 *
 * @param timestamp - Firestore Timestamp object
 * @returns Datetime string in YYYY-MM-DDTHH:mm format in EST timezone
 *
 * @example
 * // Firestore has: 2025-01-15 21:00:00 UTC (4:00 PM EST)
 * const dateTimeStr = timestampToDateTimeLocalEST(timestamp);
 * // Returns: "2025-01-15T16:00"
 */
export function timestampToDateTimeLocalEST(timestamp: Timestamp | null | undefined): string {
  if (!timestamp) {
    return "";
  }

  const date = timestamp.toDate();

  // Format in New York timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const dateParts: Record<string, string> = {};
  parts.forEach((part) => {
    dateParts[part.type] = part.value;
  });

  // Return in datetime-local format: YYYY-MM-DDTHH:mm
  return `${dateParts.year}-${dateParts.month}-${dateParts.day}T${dateParts.hour}:${dateParts.minute}`;
}

/**
 * Converts a datetime-local string (YYYY-MM-DDTHH:mm) to a Date object,
 * interpreting the input as EST/EDT timezone.
 *
 * @param dateTimeString - Datetime string in YYYY-MM-DDTHH:mm format from HTML datetime-local input
 * @returns Date object interpreted as EST/EDT
 *
 * @example
 * // User enters 4:00 PM on Jan 15, 2025 in the datetime-local input
 * const date = dateTimeLocalESTToDate("2025-01-15T16:00");
 * // Returns: Date object representing 2025-01-15 16:00:00 EST (21:00:00 UTC)
 */
export function dateTimeLocalESTToDate(dateTimeString: string): Date {
  if (!dateTimeString || dateTimeString.trim() === "") {
    throw new Error("DateTime string is required");
  }

  // Parse the datetime string (YYYY-MM-DDTHH:mm)
  const match = dateTimeString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    throw new Error(
      `Invalid datetime string format: "${dateTimeString}". Expected YYYY-MM-DDTHH:mm format.`,
    );
  }

  const [, yearStr, monthStr, dayStr, hourStr, minuteStr] = match;
  const year = Number.parseInt(yearStr, 10);
  const month = Number.parseInt(monthStr, 10);
  const day = Number.parseInt(dayStr, 10);
  const hour = Number.parseInt(hourStr, 10);
  const minute = Number.parseInt(minuteStr, 10);

  // Validate that we have valid numbers
  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute)) {
    throw new Error(
      `Invalid datetime string format: "${dateTimeString}". Expected YYYY-MM-DDTHH:mm format.`,
    );
  }

  // We need to find what UTC time equals our desired EST time
  // Start with an estimate (EST is UTC-5, EDT is UTC-4)
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  // We need to find what UTC time equals our desired EST time
  // Start with an estimate (EST is UTC-5, EDT is UTC-4)
  const offsetHours = 5; // Assume EST first
  const estimatedUTC = new Date(Date.UTC(year, month - 1, day, hour + offsetHours, minute, 0));

  // Check what this UTC time looks like in EST
  const parts = formatter.formatToParts(estimatedUTC);
  const partMap: Record<string, string> = {};
  parts.forEach((part) => {
    partMap[part.type] = part.value;
  });

  const estHour = Number.parseInt(partMap.hour, 10);
  const estMinute = Number.parseInt(partMap.minute, 10);
  const estDay = Number.parseInt(partMap.day, 10);
  const estMonth = Number.parseInt(partMap.month, 10);
  const estYear = Number.parseInt(partMap.year, 10);

  // Check if we got the right time
  if (
    estYear === year &&
    estMonth === month &&
    estDay === day &&
    estHour === hour &&
    estMinute === minute
  ) {
    return estimatedUTC;
  }

  // If not, we're probably in EDT (UTC-4), so adjust
  const adjustedUTC = new Date(Date.UTC(year, month - 1, day, hour + 4, minute, 0));
  return adjustedUTC;
}

/**
 * Gets today's date in EST timezone as a YYYY-MM-DD string.
 * Useful for date comparisons that should be timezone-consistent.
 *
 * @returns Today's date string in YYYY-MM-DD format (EST timezone)
 *
 * @example
 * getTodayEST(); // Returns: "2025-01-15" (in EST)
 */
export function getTodayEST(): string {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(now);
  const dateParts: Record<string, string> = {};
  parts.forEach((part) => {
    dateParts[part.type] = part.value;
  });

  return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
}

/**
 * Checks if a Firestore Timestamp represents a date before today in EST timezone.
 * Only compares the date portion, ignoring time.
 *
 * @param timestamp - Firestore Timestamp to check
 * @returns True if the timestamp's date (in EST) is before today (in EST)
 */
export function isDateBeforeTodayEST(timestamp: Timestamp | null | undefined): boolean {
  if (!timestamp) return false;
  const dateStr = timestampToDateString(timestamp);
  const todayStr = getTodayEST();
  return dateStr < todayStr;
}

/**
 * Checks if a Firestore Timestamp represents today's date in EST timezone.
 * Only compares the date portion, ignoring time.
 *
 * @param timestamp - Firestore Timestamp to check
 * @returns True if the timestamp's date (in EST) equals today (in EST)
 */
export function isDateTodayEST(timestamp: Timestamp | null | undefined): boolean {
  if (!timestamp) return false;
  const dateStr = timestampToDateString(timestamp);
  const todayStr = getTodayEST();
  return dateStr === todayStr;
}

/**
 * Checks if a Firestore Timestamp is within the next N days from today in EST timezone.
 * Only compares the date portion, ignoring time.
 *
 * @param timestamp - Firestore Timestamp to check
 * @param days - Number of days to look ahead
 * @returns True if the timestamp's date is between today and N days from now (inclusive)
 */
export function isDateWithinDaysEST(
  timestamp: Timestamp | null | undefined,
  days: number,
): boolean {
  if (!timestamp) return false;
  const dateStr = timestampToDateString(timestamp);
  const todayStr = getTodayEST();

  // Calculate the end date
  const today = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Add days to current time
  const futureDate = new Date(today.getTime() + days * 24 * 60 * 60 * 1000);
  const futureParts = formatter.formatToParts(futureDate);
  const futureDateParts: Record<string, string> = {};
  futureParts.forEach((part) => {
    futureDateParts[part.type] = part.value;
  });
  const futureStr = `${futureDateParts.year}-${futureDateParts.month}-${futureDateParts.day}`;

  return dateStr >= todayStr && dateStr <= futureStr;
}

/**
 * Gets the year and month from a Firestore Timestamp in EST timezone.
 *
 * @param timestamp - Firestore Timestamp
 * @returns Object with year and month numbers in EST timezone
 */
export function getYearMonthEST(
  timestamp: Timestamp | null | undefined,
): { year: number; month: number } | null {
  if (!timestamp) return null;
  const date = timestamp.toDate();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const dateParts: Record<string, string> = {};
  parts.forEach((part) => {
    dateParts[part.type] = part.value;
  });

  return {
    year: parseInt(dateParts.year, 10),
    month: parseInt(dateParts.month, 10),
  };
}

/**
 * Gets current year and month in EST timezone.
 *
 * @returns Object with current year and month numbers in EST timezone
 */
export function getCurrentYearMonthEST(): { year: number; month: number } {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
  });

  const parts = formatter.formatToParts(now);
  const dateParts: Record<string, string> = {};
  parts.forEach((part) => {
    dateParts[part.type] = part.value;
  });

  return {
    year: parseInt(dateParts.year, 10),
    month: parseInt(dateParts.month, 10),
  };
}

/**
 * Strips HTML tags from a string and returns plain text.
 * Useful for displaying HTML content in plain text contexts like summaries.
 *
 * @param html - HTML string to strip tags from
 * @returns Plain text with HTML tags removed
 *
 * @example
 * stripHtmlTags("<p>Hello <strong>world</strong></p>"); // Returns: "Hello world"
 */
export function stripHtmlTags(html: string | null | undefined): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
}
